import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MessageSquare, 
  Send, 
  Minimize2, 
  Maximize2, 
  X,
  Bot,
  User,
  ChevronDown,
  Loader2,
  Settings,
  Sparkles
} from 'lucide-react'
import type { AIModel, ChatMessage } from '@/types/interview'
import { apiRequest, buildApiUrl } from '@/config/api'

interface UniversalAIChatProps {
  currentComponent?: string
  onStatsUpdate?: (stats: any) => void
  maxResponseLength?: number
  showResponseLengthConfig?: boolean
}

const AI_MODELS: AIModel[] = [
  {
    id: 'langchain-smart',
    name: 'Smart Assistant 🤖',
    provider: 'google',
    description: 'LangChain unified agent with context-aware prompts',
    maxTokens: 8192,
    costPerToken: 0
  },
  {
    id: 'gemini-1.5-flash',
    name: 'Gemini Flash ⚡',
    provider: 'google',
    description: 'Google Gemini via LangChain - Fast, intelligent coding assistant', 
    maxTokens: 8192,
    costPerToken: 0
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini Pro 🧠',
    provider: 'google',
    description: 'Google Gemini Pro via LangChain - Advanced AI for complex problems',
    maxTokens: 8192,
    costPerToken: 0
  },
  {
    id: 'claude-3-sonnet',
    name: 'Free Coding Assistant',
    provider: 'anthropic',
    description: 'Free AI fallback for coding help and problem solving',
    maxTokens: 1000,
    costPerToken: 0
  },
  {
    id: 'gpt-4',
    name: 'Free Code Reviewer', 
    provider: 'openai',
    description: 'Free AI fallback for code review and optimization tips',
    maxTokens: 1000,
    costPerToken: 0
  },
  {
    id: 'gpt-3.5-turbo',
    name: 'Free Interview Helper',
    provider: 'openai', 
    description: 'Free AI fallback for interview preparation and tips',
    maxTokens: 1000,
    costPerToken: 0
  },
  {
    id: 'claude-3-haiku',
    name: 'Free General Assistant',
    provider: 'local',
    description: 'Free AI for general programming questions',
    maxTokens: 4096,
    costPerToken: 0
  }
]

export default function UniversalAIChat({ 
  currentComponent = 'general', 
  onStatsUpdate, 
  maxResponseLength, 
  showResponseLengthConfig = false 
}: UniversalAIChatProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [selectedModel, setSelectedModel] = useState('gemini-1.5-flash')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [totalQueries, setTotalQueries] = useState(0)
  const [totalTokens, setTotalTokens] = useState(0)
  const [configuredMaxLength, setConfiguredMaxLength] = useState(maxResponseLength || 200)
  const [showLengthConfig, setShowLengthConfig] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  useEffect(() => {
    if (maxResponseLength !== undefined) {
      setConfiguredMaxLength(maxResponseLength)
    }
  }, [maxResponseLength])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showModelSelector || showLengthConfig) {
        setShowModelSelector(false)
        setShowLengthConfig(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showModelSelector, showLengthConfig])

  const getContextPrompt = () => {
    const lengthInstruction = configuredMaxLength ? `Please keep your response under ${configuredMaxLength} characters. Be concise and direct. ` : ''
    switch (currentComponent) {
      case 'coding':
        return `${lengthInstruction}I am currently working on a coding challenge. Provide brief, focused help. `
      case 'system-design':
        return `${lengthInstruction}I am designing a system architecture. `
      case 'solution-design':
        return `${lengthInstruction}I am developing a complete application solution. `
      case 'code-review':
        return `${lengthInstruction}I am reviewing code for potential issues. `
      case 'pair-programming':
        return `${lengthInstruction}I am pair programming and collaborating. `
      case 'whiteboard':
        return `${lengthInstruction}I am discussing and designing on a whiteboard. `
      case 'video-interview':
        return `${lengthInstruction}I am in a behavioral interview discussing my background. `
      default:
        return lengthInstruction
    }
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
      modelUsed: selectedModel,
      context: currentComponent,
      component: currentComponent
    }

    setMessages(prev => [...prev, userMessage])
    const currentInput = inputValue
    setInputValue('')
    setIsLoading(true)
    setTotalQueries(prev => prev + 1)

    const contextPrompt = getContextPrompt()
    const fullPrompt = contextPrompt + currentInput
    const startTime = Date.now()

    // Save user message to database
    const saveUserMessage = async () => {
      try {
        const token = localStorage.getItem('token') || 'demo-token'
        const sessionId = localStorage.getItem('sessionId') || `session-${Date.now()}`
        
        await fetch(buildApiUrl('ai-conversations/save'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': 'test-api-key-123',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: 'user',
            content: currentInput,
            component: currentComponent,
            modelId: selectedModel,
            provider: currentModel?.provider || 'unknown',
            sessionId,
            promptTokens: Math.ceil(currentInput.length / 4), // Rough estimate
            contextData: { 
              contextPrompt,
              userAgent: navigator.userAgent
            },
            modelConfig: {
              temperature: 0.7,
              maxTokens: 500
            }
          })
        })
      } catch (error) {
        console.warn('Failed to save user message:', error)
      }
    }

    saveUserMessage()

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MCwiZW1haWwiOiJkZW1vQGV4YW1wbGUuY29tIiwicm9sZSI6ImRlbW8iLCJjb21wYW55SWQiOm51bGwsInNlc3Npb25UeXBlIjoiY29kZS1jaGFsbGVuZ2UiLCJpYXQiOjE3NTYwNzkzOTQsImV4cCI6MTc1NjY4NDE5NH0.bgDgeCNVR8RK0CkIW0LHfJEe7Fte4cnK3AIrg_keeg4'
      
      // Map UI model names to API provider/model
      const modelMapping = {
        'langchain-smart': { provider: 'langchain', model: 'gemini-flash' },
        'gemini-1.5-flash': { provider: 'langchain', model: 'gemini-flash' },
        'gemini-1.5-pro': { provider: 'langchain', model: 'gemini-pro' },
        'claude-3-sonnet': { provider: 'mockfree', model: 'coding-assistant' },
        'gpt-4': { provider: 'mockfree', model: 'code-reviewer' },
        'gpt-3.5-turbo': { provider: 'mockfree', model: 'interview-helper' },
        'claude-3-haiku': { provider: 'mockfree', model: 'coding-assistant' }
      }
      
      const apiMapping = modelMapping[selectedModel as keyof typeof modelMapping] || 
                        { provider: 'langchain', model: 'gemini-flash' }

      let response;
      
      // Use public endpoints for all models in interview sessions
      if (apiMapping.provider === 'langchain') {
        // Use specialized LangChain agent endpoint for LangChain models
        response = await fetch(buildApiUrl('ai/public/langchain/agent'), {
          method: 'POST',
          headers: {
            'X-API-Key': 'test-api-key-123',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            component: 'UniversalAIChat',
            userInput: currentInput,
            messages: messages,
            modelPreference: apiMapping.model,
            language: 'javascript',
            experienceLevel: 'intermediate',
            userTier: 'basic',
            messageType: 'general',
            additionalContext: {
              context: contextPrompt,
              currentComponent
            }
          })
        })
      } else {
        // Use public AI chat endpoint for mockfree and other providers
        response = await fetch(buildApiUrl('ai/public/chat'), {
          method: 'POST',
          headers: {
            'X-API-Key': 'test-api-key-123',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider: apiMapping.provider,
            model: apiMapping.model,
            messages: [
              {
                role: 'system',
                content: contextPrompt
              },
              {
                role: 'user',
                content: currentInput
              }
            ],
            options: {
              temperature: 0.7,
              max_tokens: Math.min(500, Math.ceil(configuredMaxLength * 1.2)) // Allow some buffer for tokens vs characters
            }
          })
        })
      }

      let aiResponseContent = ''
      let actualTokensUsed = 0

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          aiResponseContent = data.data.content
          
          // Truncate response if it exceeds the configured length
          if (configuredMaxLength && aiResponseContent.length > configuredMaxLength) {
            aiResponseContent = aiResponseContent.substring(0, configuredMaxLength - 3) + '...'
          }
          
          actualTokensUsed = data.data.usage?.total_tokens || 0
        } else {
          throw new Error(data.error?.message || 'AI API returned no data')
        }
      } else {
        throw new Error(`AI API error: ${response.status}`)
      }

      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}-response`,
        role: 'assistant',
        content: aiResponseContent,
        timestamp: new Date(),
        modelUsed: selectedModel,
        context: currentComponent,
        component: currentComponent
      }

      setMessages(prev => [...prev, assistantMessage])
      setTotalTokens(prev => prev + actualTokensUsed)

      // Save assistant response to database
      const saveAssistantMessage = async () => {
        try {
          const token = localStorage.getItem('token') || 'demo-token'
          const sessionId = localStorage.getItem('sessionId') || `session-${Date.now()}`
          const responseTime = Date.now() - startTime
          
          await fetch(buildApiUrl('ai-conversations/save'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-API-Key': 'test-api-key-123',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              role: 'assistant',
              content: aiResponseContent,
              component: currentComponent,
              modelId: selectedModel,
              provider: currentModel?.provider || 'unknown',
              sessionId,
              promptTokens: Math.ceil(currentInput.length / 4),
              responseTokens: actualTokensUsed || Math.ceil(aiResponseContent.length / 4),
              responseTimeMs: responseTime,
              cost: 0, // Free for now
              contextData: {
                contextPrompt,
                userInput: currentInput
              },
              modelConfig: {
                temperature: 0.7,
                maxTokens: 500
              },
              success: true
            })
          })
        } catch (error) {
          console.warn('Failed to save assistant message:', error)
        }
      }

      saveAssistantMessage()

      if (onStatsUpdate) {
        onStatsUpdate({
          aiQueriesCount: totalQueries + 1,
          tokensUsed: totalTokens + actualTokensUsed
        })
      }

    } catch (error) {
      console.error('AI API error:', error)
      
      // Fallback response if API fails
      const fallbackResponse = "I apologize, but I'm having trouble connecting to the AI service right now. Please try again in a moment, or check that the backend server is running."
      
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: fallbackResponse,
        timestamp: new Date(),
        modelUsed: selectedModel,
        context: currentComponent,
        component: currentComponent
      }

      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const clearChat = () => {
    setMessages([])
    setTotalQueries(0)
    setTotalTokens(0)
  }

  const currentModel = AI_MODELS.find(m => m.id === selectedModel)

  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors z-50"
        title="Open AI Assistant"
      >
        <MessageSquare className="w-6 h-6" />
        {totalQueries > 0 && (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {totalQueries > 99 ? '99+' : totalQueries}
          </span>
        )}
      </motion.button>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className={`fixed bottom-6 right-6 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[500px]'
      }`}
      data-testid="ai-chat-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50 rounded-t-lg">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Assistant</h3>
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>{currentModel?.name}</span>
              <span>•</span>
              <span>{totalQueries} queries</span>
              {totalTokens > 0 && (
                <>
                  <span>•</span>
                  <span>{totalTokens.toLocaleString()} tokens</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="relative">
            <button
              onClick={() => setShowModelSelector(!showModelSelector)}
              className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              title="Select AI Model"
            >
              <span className="text-gray-700">{currentModel?.name}</span>
              <ChevronDown className="w-3 h-3 text-gray-500" />
            </button>
            
            <AnimatePresence>
              {showModelSelector && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                >
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-700 mb-2">Select AI Model</div>
                    {AI_MODELS.map(model => (
                      <button
                        key={model.id}
                        onClick={() => {
                          setSelectedModel(model.id)
                          setShowModelSelector(false)
                        }}
                        className={`w-full text-left p-2 rounded text-xs hover:bg-gray-50 transition-colors ${
                          selectedModel === model.id ? 'bg-blue-50 border border-blue-200' : ''
                        }`}
                      >
                        <div className="font-medium">{model.name}</div>
                        <div className="text-gray-500 text-xs">{model.description}</div>
                        <div className="text-gray-400 text-xs">
                          ${model.costPerToken}/token • {model.maxTokens} max tokens
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Response Length Configuration */}
          {showResponseLengthConfig && (
            <div className="relative">
              <button
                onClick={() => setShowLengthConfig(!showLengthConfig)}
                className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                title="Configure Response Length"
              >
                <Settings className="w-3 h-3 text-gray-500" />
                <span className="text-gray-700">{configuredMaxLength}ch</span>
              </button>
              
              {showLengthConfig && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3">
                  <div className="text-xs font-medium text-gray-700 mb-2">Response Length Limit</div>
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="50"
                      max="1000"
                      step="50"
                      value={configuredMaxLength}
                      onChange={(e) => setConfiguredMaxLength(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>50</span>
                      <span className="font-medium">{configuredMaxLength} characters</span>
                      <span>1000</span>
                    </div>
                    <div className="flex space-x-1 mt-2">
                      {[100, 200, 300, 500].map(preset => (
                        <button
                          key={preset}
                          onClick={() => setConfiguredMaxLength(preset)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            configuredMaxLength === preset 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 h-80">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">Start a conversation with your AI assistant</p>
                <p className="text-xs text-gray-400 mt-1">
                  Context: {currentComponent.charAt(0).toUpperCase() + currentComponent.slice(1)}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} items-start space-x-2`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' ? 'bg-blue-600' : 'bg-gray-600'
                      }`}>
                        {message.role === 'user' ? (
                          <User className="w-3 h-3 text-white" />
                        ) : (
                          <Bot className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className={`px-3 py-2 rounded-lg text-sm ${
                        message.role === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <div className="whitespace-pre-wrap">
                          {message.content.includes('```') ? (
                            <div dangerouslySetInnerHTML={{
                              __html: message.content
                                .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-gray-800 text-green-400 p-3 rounded mt-2 mb-2 overflow-x-auto"><code class="text-sm">$2</code></pre>')
                                .replace(/`([^`]+)`/g, '<code class="bg-gray-200 text-red-600 px-1 rounded text-sm">$1</code>')
                            }} />
                          ) : (
                            message.content
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${message.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {message.modelUsed && ` • ${AI_MODELS.find(m => m.id === message.modelUsed)?.name}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start space-x-2">
                      <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                      <div className="bg-gray-100 px-3 py-2 rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-2">
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Ask about ${currentComponent}...`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors z-10 relative"
                title="Send message"
                data-testid="ai-send-button"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="w-full mt-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Clear chat history
              </button>
            )}
          </div>
        </>
      )}
    </motion.div>
  )
}